import { useMemo, useState } from 'react'
import { Filter, Target } from 'lucide-react'
import type { BlindStat, HuntType, ZoneStat } from '@/shared/domain'
import {
  harvestContainerLabel,
  harvestRateLabel,
  isUpland,
  locationEmptyDescription,
  locationEmptyHeading,
  locationRowNoun,
  locationUnitLabelPlural,
} from '@/shared/domain'
import { EmptyState } from './empty-state'
import { ErrorState } from './error-state'
import { StatTile } from './stat-tiles'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@/shared/ui/primitives'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/shared/ui/primitives/card'
import { Skeleton } from '@/shared/ui/primitives/skeleton'

export type BlindPerformanceZoneView = 'zones' | 'blinds'
type SortKey = 'totalHarvest' | 'huntsCount' | 'averageHarvest' | 'name'

type PropertyOption = {
  id: string
  name: string
}

type BlindPerformanceCardProps = {
  title?: string
  huntType?: HuntType
  blinds: BlindStat[]
  zones: ZoneStat[]
  zoneView: BlindPerformanceZoneView
  isLoading: boolean
  error?: boolean
  onZoneViewChange: (view: BlindPerformanceZoneView) => void
  properties?: PropertyOption[]
  selectedPropertyId?: string
  onPropertyChange?: (id: string) => void
  fixedHeight?: boolean
}

function BlindPerformanceSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-28 w-full" />
      ))}
    </div>
  )
}

function BlindSubCard({
  item,
  isZone,
  containerLabel,
  rateLabel,
}: {
  item: BlindStat | ZoneStat
  isZone: boolean
  containerLabel: string
  rateLabel: string
}) {
  const subtitle = isZone
    ? `${(item as ZoneStat).blindCount} ${(item as ZoneStat).blindCount === 1 ? 'blind' : 'blinds'}`
    : `${item.zoneName ?? 'Unknown Zone'} • ${locationRowNoun((item as BlindStat).locationType)}`

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3">
        <p className="font-display text-base font-semibold leading-tight">{item.name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <StatTile value={item.totalHarvest} label={containerLabel} color="var(--blaze)" />
        <StatTile value={item.huntsCount} label="Hunts" />
        <StatTile
          value={item.averageHarvest.toFixed(1)}
          label={rateLabel}
          color="var(--sage)"
        />
      </div>
    </div>
  )
}

export function BlindPerformanceCard({
  title = 'Blind Performance',
  huntType,
  blinds,
  zones,
  zoneView,
  isLoading,
  error = false,
  onZoneViewChange,
  properties,
  selectedPropertyId,
  onPropertyChange,
  fixedHeight = false,
}: BlindPerformanceCardProps) {
  const [sortKey, setSortKey] = useState<SortKey>('totalHarvest')
  const isZone = zoneView === 'zones'
  const items = useMemo(() => (isZone ? zones : blinds), [isZone, zones, blinds])
  const showPropertyFilter =
    properties !== undefined && onPropertyChange !== undefined && selectedPropertyId !== undefined

  const containerLabel = huntType ? harvestContainerLabel(huntType) : 'Total Birds'
  const rateLabel = huntType ? harvestRateLabel(huntType) : 'B/H/H'

  const sortOptions = useMemo<Array<{ value: SortKey; label: string }>>(
    () => [
      { value: 'totalHarvest', label: containerLabel },
      { value: 'huntsCount', label: 'Hunts' },
      { value: 'averageHarvest', label: rateLabel },
      { value: 'name', label: 'Name' },
    ],
    [containerLabel, rateLabel],
  )

  const sortedItems = useMemo(() => {
    const copy = [...items]
    copy.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name)
      return (b[sortKey] as number) - (a[sortKey] as number)
    })
    return copy
  }, [items, sortKey])

  return (
    <Card className={fixedHeight ? 'flex flex-col min-h-[580px]' : undefined}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardAction>
          <div className="flex items-center gap-2">
            {showPropertyFilter ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" aria-label="Filters" className="bg-card">
                    <Filter className="size-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 space-y-4">
                  <section className="space-y-2">
                    <h4 className="text-sm font-semibold">Property</h4>
                    <Select value={selectedPropertyId} onValueChange={onPropertyChange}>
                      <SelectTrigger aria-label="Property filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </section>
                </PopoverContent>
              </Popover>
            ) : null}
            <ToggleGroup
              type="single"
              size="sm"
              value={zoneView}
              onValueChange={(value) => {
                if (value) onZoneViewChange(value as BlindPerformanceZoneView)
              }}
              aria-label="View mode"
            >
              <ToggleGroupItem value="blinds">
                {huntType ? locationUnitLabelPlural(huntType) : 'Blinds'}
              </ToggleGroupItem>
              {huntType && isUpland(huntType) ? null : (
                <ToggleGroupItem value="zones">Zones</ToggleGroupItem>
              )}
            </ToggleGroup>
          </div>
        </CardAction>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
        <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
          <SelectTrigger aria-label="Sort by" className="w-44 bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isLoading ? (
          <BlindPerformanceSkeleton />
        ) : error ? (
          <ErrorState
            title="Performance unavailable"
            description="Unable to load performance data."
          />
        ) : sortedItems.length === 0 ? (
          <EmptyState
            icon={Target}
            heading={huntType ? locationEmptyHeading(huntType) : 'No blind data'}
            description={
              huntType
                ? locationEmptyDescription(huntType)
                : 'No blind or zone performance data is available.'
            }
          />
        ) : (
          <div
            style={
              fixedHeight
                ? { height: '450px', overflowY: 'auto', paddingRight: '4px' }
                : undefined
            }
          >
            <div className="flex flex-col gap-2">
              {sortedItems.map((item) => (
                <BlindSubCard
                  key={item.id}
                  item={item}
                  isZone={isZone}
                  containerLabel={containerLabel}
                  rateLabel={rateLabel}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
